import { Radio as AriakitRadio, VisuallyHidden } from '@ariakit/react';
import {
  FC,
  forwardRef,
} from 'react';
import { withSuffix } from 'src/common/utils/dataAttributeHelpers';
import { useRadio } from '../RadioContext';

/**
 * Converts the input value to a string if it's a boolean, otherwise returns the value unchanged.
 *
 * @param value - The value to parse, which can be a boolean, number, or string.
 * @returns The parsed value: a string if the input was a boolean, or the original value otherwise.
 */
export const parseRadioValue = (
  value: boolean | number | string | null | undefined,
): string | number => (
  typeof value === 'boolean' ? String(value) : value ?? ''
);

type RadioProps = {
  value: string | number | boolean;
  children: any;
  className?: string;
  name?: string;
  disabled?: boolean;
};
const Radio: FC<RadioProps> = forwardRef<HTMLInputElement, RadioProps>(({
  value,
  children,
  name,
  disabled,
  className,
  ...props
}, ref) => {
  const { setFocus, setValue, value: state } = useRadio();
  return (
    <label {...props} className={className}>
      {children}
      <VisuallyHidden>
        <AriakitRadio
          value={parseRadioValue(value)}
          clickOnEnter
          clickOnSpace
          name={name}
          ref={ref}
          onFocusVisible={() => setFocus(value)}
          onChange={() => setValue(value)}
          data-test={withSuffix(parseRadioValue(value), 'radio')}
          checked={parseRadioValue(value) === state}
          disabled={disabled}
        />
      </VisuallyHidden>
    </label>
  );
});
export default Radio;
